import { ensureTrailingSlash } from "../../../utils/anchor-links"
import { getSessionLocale } from "../../../utils/local-storage-user-info"
import {
  DEFAULT_LOCALE,
  isAlternativeLocale,
  Locale,
} from "../../../utils/locales"
import { getLocalizedPagePath } from "../../../utils/localized-routes"
import { TREMENDOUS_MARKETING_URL } from "../../../utils/urls"

function resolveTargetLocale(currentPageLocale: unknown): Locale {
  // remain on international locale if we're already on it
  if (isAlternativeLocale(currentPageLocale)) {
    return currentPageLocale
  }

  // scenario: on english page, but seeing link to preferred locale
  // return preferred locale so the route map transforms the link href
  if (isAlternativeLocale(getSessionLocale())) {
    return getSessionLocale()
  }

  return DEFAULT_LOCALE
}

/**
 * Sanitizes a given href by ensuring trailing slash and prepending
 * locale prefix if the current page uses a non-English locale.
 *
 * @param href target href, most frequently provided to <UniversalLink>
 * @param currentPageLocale optional page locale, usually found via `useLingui`
 * @returns link href string
 */
export function resolveLinkHref(href: string, currentPageLocale?: string) {
  const hasDomain = href.startsWith("http")
  const isMailTo = href.startsWith("mailto:")
  const isAnchor = href.startsWith("#")
  const isExternalUrl = hasDomain && !href.startsWith(TREMENDOUS_MARKETING_URL)
  const isRelativePath = !hasDomain && !href.startsWith("/")

  if (isAnchor || isMailTo || isExternalUrl) {
    return href
  }

  if (isRelativePath) {
    return ensureTrailingSlash(href)
  }

  const url = new URL(href, TREMENDOUS_MARKETING_URL)
  url.pathname = ensureTrailingSlash(url.pathname)

  // localization route map look up
  const locale = resolveTargetLocale(currentPageLocale)
  const localizedPagePath = getLocalizedPagePath(url.pathname, locale)

  // if available, upgrade to localized page
  if (localizedPagePath) {
    url.pathname = localizedPagePath
  }

  // return relative URL without domain
  return url.pathname + url.search + url.hash
}

export function getCanonicalUrl(href: string) {
  // throw an error if pathname is malformed
  if (!href.startsWith("/") || href.includes("?")) {
    throw new Error(
      `getCanonicalUrl was called with an invalid pathname: ${href}`,
    )
  }

  const sanitizedHref = resolveLinkHref(href)

  return `${TREMENDOUS_MARKETING_URL}${sanitizedHref}`.toLowerCase()
}
